import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useUserContext } from '@/context';

export default function useMediaDevice() {

  const { profile } = useUserContext();
  const [t] = useTranslation(['common', 'face']);

  const isSupported = 'mediaDevices' in window.navigator;
  let initError = !profile?.changeProfilePicturePermission ?
    t('face:errors.capture-profile-permission') : null;
  if (!initError && !isSupported) {
    initError = t('face:errors.capture-support');
  }
  const [stream, setStream] = useState<MediaStream>();
  const [error, setError] = useState(initError);
  const [requested, setRequested] = useState(false);

  const requestCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 } });
      setRequested(true);
      setStream(mediaStream);
      return mediaStream;
    }
    catch (e) {
      if (e.name === 'NotAllowedError') {
        return setError(t('face:errors.capture-not-allowed'));
      }
      if ((e.message as string)?.toLowerCase()?.includes('permission')) {
        return setError(t('face:errors.capture-browser-permission'));
      }
      setError(t('common:labels.unknown-err-code', { code: e.name }));
    }
  }, [t]);

  const stopStream = useCallback(() => {
    if (!stream) return;
    const tracks = stream.getTracks();
    for (const track of tracks) {
      if (track.readyState === 'live') {
        track.stop();
      }
    }
  }, [stream]);

  return {
    requested,
    requestCamera,
    isSupported,
    error,
    setError,
    stopStream
  };
}
